import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { makeStyles, CircularProgress } from '@material-ui/core';
import { UserIdentity } from '@backstage/core-components';
import {
  googleAuthApiRef,
  useApi,
  SignInPageProps,
} from '@backstage/core-plugin-api';
import { LogoFull } from '../nav/LogoFull';
import { AuthGraphMotif } from './AuthGraphMotif';

// Mensagens em pt-BR pros erros nomeados que o loginPopup/OAuthRequestManager
// do @backstage/core-app-api de fato emitem (ver node_modules — não são
// invenção). PopupRejectedError fica de fora de propósito: é o retorno do
// instantPopup automático no mount sem gesto do usuário (bloqueado pelo
// browser) — o SingleSignInPage original também o esconde.
const ERROR_MESSAGES: Record<string, string> = {
  PopupClosedError:
    'Não foi possível entrar — a janela do Google foi fechada. Tente de novo.',
  RejectedError: 'Você recusou o pedido de login. Tente de novo.',
};

const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    display: 'grid',
    gridTemplateColumns: '1.15fr 0.85fr',
    background: '#1a1717',
    color: '#f6f6f6',
    '@media (max-width: 860px)': {
      gridTemplateColumns: '1fr',
    },
  },
  // Painel esquerdo — hero com o emblema Yggdrasil (tree-of-life) atrás.
  hero: {
    position: 'relative',
    overflow: 'hidden',
    background: '#1a1717',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: 44,
    '@media (max-width: 860px)': {
      minHeight: 240,
      padding: 30,
    },
    // Glows de atmosfera + scrim inferior pra manter o texto legível sobre as
    // raízes do emblema. Fica ACIMA do canvas, ABAIXO do conteúdo (z-index).
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      background:
        'radial-gradient(55% 38% at 20% 20%, rgba(219,35,40,.16), transparent 70%),' +
        'radial-gradient(50% 45% at 86% 84%, rgba(79,59,178,.16), transparent 70%),' +
        'linear-gradient(to top, rgba(20,17,17,.90) 2%, rgba(20,17,17,.35) 26%, transparent 46%)',
    },
  },
  heroContent: {
    position: 'relative',
    zIndex: 2,
  },
  wordmark: {
    position: 'relative',
    zIndex: 2,
    '& svg': {
      height: 30,
      width: 'auto',
      display: 'block',
    },
  },
  heroFoot: {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  signature: {
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 500,
    fontSize: '0.82rem',
    letterSpacing: '0.34em',
    textTransform: 'uppercase',
    color: '#f07575',
  },
  lead: {
    margin: 0,
    fontSize: '1.9rem',
    lineHeight: 1.15,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    maxWidth: '15ch',
    color: '#fff',
    '& em': { color: '#f07575', fontStyle: 'normal' },
  },
  // Painel direito — card de auth.
  panel: {
    background: '#2c2929',
    borderLeft: '1px solid #3a3535',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '48px 44px',
    '@media (max-width: 860px)': {
      borderLeft: 'none',
      borderTop: '1px solid #3a3535',
    },
  },
  card: {
    width: '100%',
    maxWidth: 340,
    margin: '0 auto',
  },
  title: {
    margin: '0 0 6px',
    fontSize: '1.35rem',
    fontWeight: 700,
    letterSpacing: '-0.015em',
    color: '#fff',
  },
  sub: {
    margin: '0 0 30px',
    fontSize: '0.92rem',
    color: '#b0aeae',
    lineHeight: 1.5,
  },
  error: {
    width: '100%',
    marginBottom: 20,
    padding: '10px 14px',
    borderRadius: 8,
    background: 'rgba(219, 35, 40, 0.12)',
    border: '1px solid rgba(219, 35, 40, 0.4)',
    color: '#f7abab',
    fontSize: '0.85rem',
  },
  button: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 11,
    fontFamily: '"Work Sans", system-ui, sans-serif',
    fontWeight: 600,
    fontSize: '0.95rem',
    textTransform: 'none',
    color: '#fff',
    background: '#db2328',
    border: 'none',
    borderRadius: 9,
    padding: '13px 20px',
    cursor: 'pointer',
    '&:hover': { background: '#af2418' },
    '&:disabled': { cursor: 'default', opacity: 0.75 },
    '&:focus-visible': { outline: '2px solid #f07575', outlineOffset: 2 },
  },
  restrict: {
    margin: '16px 0 0',
    fontSize: '0.8rem',
    color: '#8c8989',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#188f34',
  },
  divider: {
    margin: '28px 0',
    border: 'none',
    borderTop: '1px solid #3a3535',
  },
  foot: {
    fontSize: '0.72rem',
    color: '#696565',
    textAlign: 'center',
    lineHeight: 1.6,
  },
});

// Réplica do fluxo de auth do SingleSignInPage (@backstage/core-components,
// ver dist/layout/SignInPage/SignInPage.esm.js): getBackstageIdentity
// optional no mount (sessão existente completa sem clique), instantPopup
// automático em seguida (auto — silenciosamente bloqueado pelo browser na
// maioria dos casos, daí o PopupRejectedError escondido), e clique manual
// cai no mesmo caminho com showPopup. Sucesso monta a IdentityApi via
// UserIdentity.create, igual ao original.
export const GoogleSignInPage = ({ onSignInSuccess }: SignInPageProps) => {
  const classes = useStyles();
  const authApi = useApi(googleAuthApiRef);
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get('error');
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);

  const login = async ({ checkExisting }: { checkExisting?: boolean }) => {
    setLoading(true);
    try {
      let identityResponse;
      if (checkExisting) {
        identityResponse = await authApi.getBackstageIdentity({
          optional: true,
        });
      }
      if (!identityResponse && !errorParam) {
        identityResponse = await authApi.getBackstageIdentity({
          instantPopup: true,
        });
      }
      if (!identityResponse) {
        return;
      }
      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
    } catch (err) {
      if ((err as Error).name !== 'PopupRejectedError') {
        setError(err as Error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (errorParam) {
      setError(new Error(errorParam));
    }
    login({ checkExisting: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorMessage = error && (ERROR_MESSAGES[error.name] ?? error.message);

  return (
    <div className={classes.page}>
      <div className={classes.hero}>
        <AuthGraphMotif />
        <div className={classes.wordmark}>
          <LogoFull />
        </div>
        <div className={classes.heroFoot}>
          <span className={classes.signature}>Yggdrasil</span>
          <h1 className={classes.lead}>
            O mapa vivo dos <em>serviços</em> da Apoia.se
          </h1>
        </div>
      </div>

      <div className={classes.panel}>
        <div className={classes.card}>
          <h2 className={classes.title}>Entrar</h2>
          <p className={classes.sub}>
            Catálogo de serviços da Apoia.se. Autenticação via workspace Google.
          </p>

          {errorMessage && <div className={classes.error}>{errorMessage}</div>}

          <button
            type="button"
            className={classes.button}
            disabled={loading}
            onClick={() => login({})}
          >
            {loading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            Entrar com Google
          </button>
          <p className={classes.restrict}>
            <span className={classes.dot} /> Acesso restrito a contas @apoia.se
          </p>

          <hr className={classes.divider} />
          <p className={classes.foot}>Yggdrasil · catálogo de serviços da Apoia.se</p>
        </div>
      </div>
    </div>
  );
};
